export const SERVICE_COHORT_CHECKOUT_FLOW = 'service_cohort';

export const MAX_COHORT_SESSIONS = 24;

export const DEFAULT_COHORT_JOIN_RELEASE_MINUTES = 15;

export type CohortSessionInput = {
  sessionIndex: number;
  title?: string | null;
  startsAt: Date;
  durationMinutes: number;
  timezone: string;
  meetingProvider?: string | null;
  manualMeetingUrl?: string | null;
};

export function computeCohortSessionEndsAt(startsAt: Date, durationMinutes: number): Date {
  return new Date(startsAt.getTime() + durationMinutes * 60_000);
}

export function computeCohortJoinReleaseAt(
  startsAt: Date,
  joinLinkReleaseMinutes = DEFAULT_COHORT_JOIN_RELEASE_MINUTES,
): Date {
  return new Date(startsAt.getTime() - joinLinkReleaseMinutes * 60_000);
}

export function isCohortSessionJoinAvailable(
  startsAt: Date,
  durationMinutes: number,
  joinLinkReleaseMinutes = DEFAULT_COHORT_JOIN_RELEASE_MINUTES,
  now = new Date(),
): boolean {
  const releaseAt = computeCohortJoinReleaseAt(startsAt, joinLinkReleaseMinutes);
  const endsAt = computeCohortSessionEndsAt(startsAt, durationMinutes);
  return now.getTime() >= releaseAt.getTime() && now.getTime() < endsAt.getTime();
}

export function validateCohortSessions(sessions: CohortSessionInput[]): string[] {
  const errors: string[] = [];
  if (sessions.length === 0) errors.push('Add at least one session');
  if (sessions.length > MAX_COHORT_SESSIONS) {
    errors.push(`At most ${MAX_COHORT_SESSIONS} sessions allowed`);
  }
  for (const session of sessions) {
    if (!session.startsAt || Number.isNaN(session.startsAt.getTime())) {
      errors.push(`Session ${session.sessionIndex + 1} needs a valid start time`);
    }
    if (!session.durationMinutes || session.durationMinutes < 15 || session.durationMinutes > 480) {
      errors.push(`Session ${session.sessionIndex + 1} duration must be 15–480 minutes`);
    }
  }
  return errors;
}

export function validateCohortOfferPublish(input: {
  title: string;
  priceCents: number;
  refundPolicyText?: string | null;
  capacity?: number | null;
  sessions: CohortSessionInput[];
}): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!input.title.trim()) errors.push('Title is required');
  if (input.priceCents > 0 && !input.refundPolicyText?.trim()) {
    errors.push('Refund policy is required for paid offers');
  }
  if (!input.capacity || input.capacity < 1) {
    errors.push('Enrollment capacity must be at least 1');
  }
  errors.push(...validateCohortSessions(input.sessions));

  const now = Date.now();
  const futureSessions = input.sessions.filter((s) => s.startsAt.getTime() > now);
  if (futureSessions.length === 0 && input.sessions.length > 0) {
    errors.push('At least one session must be scheduled in the future');
  }

  return { errors, warnings };
}

export const SERVICE_COHORT_ANALYTICS_EVENT = {
  ENROLLED: 'cohort.enrolled',
} as const;
